import { useGLTF } from "@react-three/drei";
import React from "react";
import { useConfigurationOptionsContext } from "../../../../../app/providers/DoorOptionsProvider";
import { isUHandleCode } from "../../../../../app/providers/doorHandleProfiles";

import { getFullAssetPath } from "shared/utils/assets";
import { DoorComponentProps } from "../../Door";
import Beam from "../components/Beam";
import Glass from "../components/Glass";
import JHandle from "../components/JHandle";
import Kozijn from "../components/Kozijn";
import Lock from "../components/Lock";
import UHandle from "../components/UHandle";
import { HandleSmalRond } from "../taats/handles/HandleSmalRond";
import { DeIjsselLayout } from "../layouts/DeIjsselLayout";
import { DeMaasLayout } from "../layouts/DeMaasLayout";
import { DeWaalCutLayouts } from "../layouts/DeWaalCutLayouts";
import { DeWaalLayoutXLXXL } from "../layouts/DeWaalLayoutXLXXL";
import { ScharnierVerticalLayout } from "../layouts/ScharnierVerticalLayout";
import { type VerticalBeamPosition } from "entities/configurator/model/layoutMeta";
import Frame from "./Frame";
import { ScharnierGLTFResult } from "./ScharnierType";

interface ScharnierProps extends DoorComponentProps {
  secondary?: boolean;
  secondaryDoorDoubleJHandle?: boolean;
  rotateNewLayouts?: boolean;
  verticalBeamPosition?: VerticalBeamPosition;
}

const Scharnier: React.FC<ScharnierProps> = ({
  materials,
  position,
  rotation,
  secondary = false,
  index = 0,
  kozijnSettings,
  frameSettings,
  lockSettings,
  secondaryDoorDoubleJHandle,
  rotateNewLayouts = false,
  verticalBeamPosition,
}: ScharnierProps) => {
  const { nodes } = useGLTF(
    getFullAssetPath("/objects-v3/scharnierdeur_v6.glb"),
  ) as ScharnierGLTFResult;

  const {
    selectedDoor,
    handle,
    getConfigurationBeams,
    getConfigurationWidth,
    getConfigurationHeight,
    isDeMaasLayout,
    isDeWaalLayout,
    isDeIjsselLayout,
    getConfigurationColor,
    rotateDeWaal23,
    rotateDeMaas2Dubbel,
    isLeafStyleDeWaalLayout,
  } = useConfigurationOptionsContext();

  const chosenMaterial = materials[getConfigurationColor()];
  const doorWidth = getConfigurationWidth(index);
  const doorHeight = getConfigurationHeight(index);
  const frameScaleX = frameSettings?.generalScaleX ?? 1;

  const showSmalRondHandle =
    handle?.code === "normalhandletaatsrond" ||
    handle?.code === "normalhandlescharnierronddubbel";
  const showSmalRondDubbelHandle =
    handle?.code === "normalhandlescharnierronddubbel";
  const smalRondHandleRotationY =
    secondary && showSmalRondDubbelHandle ? Math.PI : 0;
  const showHandleOnSecondary =
    !secondary ||
    handle?.code.includes("dubbel") ||
    selectedDoor?.code === "scharnierdeur1paneel";
  const rotateLayouts = rotateNewLayouts || secondary;
  const isVerticalHandle = isUHandleCode(handle?.code);
  const isJHandle = !!handle?.code.includes("taats") && !showSmalRondHandle;
  const showsLockHandle =
    !showSmalRondHandle && !isVerticalHandle && !isJHandle;
  const currentHandleOwnsAnchorBeams =
    showsLockHandle || (isVerticalHandle && showHandleOnSecondary);
  const configuredBeams = getConfigurationBeams(!currentHandleOwnsAnchorBeams);

  let activeHandle: React.ReactNode = null;

  if (isVerticalHandle) {
    activeHandle = showHandleOnSecondary ? (
      <group rotation-y={frameSettings?.UHandleReverse ? Math.PI : 0}>
        <UHandle
          doorNodes={nodes}
          materials={materials}
          reverse={frameSettings?.UHandleReversePos ?? true}
          showBackHandle={frameSettings?.UHandleShowBackHandle}
          shiftXValue={frameSettings?.UHandleShiftX ?? 0}
          schuifdeur={frameSettings?.UHandleShowFrontHandle}
          index={index}
        />
      </group>
    ) : null;
  } else if (isJHandle) {
    activeHandle = showHandleOnSecondary ? (
      <group
        position-z={frameSettings?.JhandleGeneralShiftZ ?? 0}
        rotation-y={frameSettings?.JhandleGeneralRotationY ? Math.PI : 0}
      >
        <JHandle
          doorNodes={nodes}
          materials={materials}
          index={index}
          reverse={frameSettings?.JHandleReverse ?? false}
          shiftZValue={frameSettings?.JhandleShiftZ ?? 0}
          shiftXValue={frameSettings?.JhandleShiftX ?? 0}
          secondaryDoorDoubleJHandle={secondaryDoorDoubleJHandle}
        />
      </group>
    ) : null;
  } else if (showsLockHandle) {
    activeHandle = (
      <Lock
        doorNodes={nodes}
        materials={materials}
        secondary={secondary}
        type={handle ? handle.code : ""}
        index={index}
        {...lockSettings}
      />
    );
  }

  return (
    <group
      scale-y={frameSettings?.generalScaleY ?? 1}
      dispose={null}
      position={position}
    >
      <group rotation-y={rotateLayouts ? Math.PI : 0}>
        {isDeMaasLayout && (
          <group rotation-y={rotateDeMaas2Dubbel ? Math.PI : 0}>
            <DeMaasLayout
              nodes={nodes}
              chosenMaterial={chosenMaterial}
              doorIndex={index}
            />
          </group>
        )}

        {/* De Waal */}
        {isDeWaalLayout && (
          <group rotation-y={rotateDeWaal23 ? Math.PI : 0}>
            {isLeafStyleDeWaalLayout ? (
              <DeWaalCutLayouts
                nodes={nodes}
                chosenMaterial={chosenMaterial}
                doorIndex={index}
              />
            ) : (
              <DeWaalLayoutXLXXL
                nodes={nodes}
                chosenMaterial={chosenMaterial}
                doorIndex={index}
              />
            )}
          </group>
        )}

        {isDeIjsselLayout && (
          <DeIjsselLayout
            nodes={nodes}
            chosenMaterial={chosenMaterial}
            doorIndex={index}
          />
        )}
      </group>
      {showSmalRondHandle && showHandleOnSecondary && (
        <group rotation-y={smalRondHandleRotationY}>
          <HandleSmalRond
            nodes={nodes}
            chosenMaterial={chosenMaterial}
            doorIndex={index}
          />
        </group>
      )}

      <group>
        <Kozijn
          doorNodes={nodes}
          materials={materials}
          index={index}
          existingFrame={false}
          {...kozijnSettings}
        />
      </group>
      <group scale-x={frameScaleX}>
        <ScharnierVerticalLayout
          nodes={nodes}
          material={chosenMaterial}
          width={doorWidth}
          height={doorHeight}
          semanticPosition={verticalBeamPosition}
          frameSettings={frameSettings}
        />
      </group>
      <group scale-x={frameScaleX}>
        <Frame
          doorNodes={nodes}
          materials={materials}
          index={index}
          {...frameSettings}
        />
        <Glass doorNodes={nodes} materials={materials} index={index} />
        <group rotation={rotation}>
          {activeHandle}
          {configuredBeams.map((beam, i) => {
            return (
              <Beam
                id={beam.id}
                key={`scharnier-beam-${i}`}
                doorNodes={nodes}
                materials={materials}
                width={doorWidth}
                height={beam.height / 1000}
              />
            );
          })}
        </group>
      </group>
    </group>
  );
};

export default Scharnier;
